import React, { useState, useMemo, useEffect } from 'react';
import { showToast } from '@/components/ui/Toast';
import { getDocs, addDoc, getDoc, setDoc, doc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { Appliance } from '@/types';
import { useTranslation } from 'react-i18next';
import {
  AppState,
  AppAction,
  KanbanTask,
  TaskStatus,
  TemplateType,
  TaskScope,
  PriorityTier,
  RecurrenceRangeType
} from '@/types';
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import {
  Wrench, History, Calendar,
  Loader2, Activity, LayoutDashboard,
  ArrowUpRight, TrendingUp, Plus, ShieldAlert,
  Home, ArrowRight, Cpu, X, DollarSign, Sparkles
} from 'lucide-react';
import { useAiAction } from '@/hooks/useAiAction';
import { Button } from '@/components/ui/Button';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { useNavigate } from 'react-router-dom';
import { NLPMaintenanceInput } from '../features/maintenance-engine/components/NLPMaintenanceInput';
import { HistoricalTaskCreator } from '../features/maintenance-engine/components/HistoricalTaskCreator';
import { ParsedMaintenanceIntent } from '../features/maintenance-engine/utils/maintenanceParser';

interface MaintenanceProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const MAINTENANCE_BOARD_ID = 'maintenance-board';

/**
 * 🛠️ UTILITY: Mapping layer for Loose Data to Strict KanbanTask Types
 * This resolves the "createTask" argument errors by ensuring objects are correctly structured.
 */
const mapDataToTaskObjects = (data: any) => {
  const recurrence: KanbanTask['recurrence'] = data.recurrence ? {
    isEnabled: data.recurrence.isEnabled ?? false,
    interval: data.recurrence.interval ?? 0,
    // Use optional chaining for frequency and cast
    frequency: (data.recurrence.frequency as RecurrenceRangeType | null) ?? null,
    completionTrigger: data.recurrence.completionTrigger ?? false,
    lastResetDate: data.recurrence.lastResetDate as string | undefined,
  } : undefined;

  const consumption: KanbanTask['consumption'] = data.consumption ? {
    quantity: data.consumption.quantity ?? 1,
    remainingQuantity: data.consumption.remainingQuantity ?? 1,
    unit: data.consumption.unit ?? 'unit',
    // Use optional chaining for safe access to nested properties
    stockLevel: data.consumption.stockLevel as number | undefined,
    averageCycle: data.consumption.averageCycle as number | undefined,
    lastAddedHistory: data.consumption.lastAddedHistory as string | undefined,
    dayBetweenRecurrence: data.consumption.dayBetweenRecurrence as number[] | undefined,
    cost: data.consumption.cost as number | undefined, // This line is specifically addressed
  } : undefined;

  return { recurrence, consumption };
};

export const Maintenance: React.FC<MaintenanceProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'log' | 'schedule' | 'history' | 'appliances' | 'budget'>('dashboard');
  const [showManual, setShowManual] = useState(false);
  const [appliances, setAppliances] = useState<Appliance[]>([]);
  const [appliancesLoading, setAppliancesLoading] = useState(false);
  const [showAddAppliance, setShowAddAppliance] = useState(false);
  const [appName, setAppName] = useState('');
  const [appMake, setAppMake] = useState('');
  const [appModel, setAppModel] = useState('');
  const [appSerial, setAppSerial] = useState('');
  const [appRoom, setAppRoom] = useState('Kitchen');
  const [appWarranty, setAppWarranty] = useState('');
  const [appManualUrl, setAppManualUrl] = useState('');
  const [appNotes, setAppNotes] = useState('');
  const [isSavingAppliance, setIsSavingAppliance] = useState(false);

  const [budgetTasks, setBudgetTasks] = useState<KanbanTask[]>([]);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [annualBudgetCap, setAnnualBudgetCap] = useState<number>(0);
  const [budgetCapInput, setBudgetCapInput] = useState('');
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  const hasActiveHousehold = !!state.currentHouseholdId;

  // 🤖 AI Actions
  const { callAction: callAi } = useAiAction(state.currentHouseholdId);
  const [aiCheckupResult, setAiCheckupResult] = useState<{ predictions: Array<{ item: string; urgency: string; estimatedCost: number }> } | null>(null);
  const [aiCheckupLoading, setAiCheckupLoading] = useState(false);
  const [troubleshootTarget, setTroubleshootTarget] = useState<string | null>(null);
  const [troubleshootResult, setTroubleshootResult] = useState<{ diagnosis: string; steps: string[] } | null>(null);
  const [troubleshootLoading, setTroubleshootLoading] = useState(false);

  const handleAiCheckup = async () => {
    setAiCheckupLoading(true);
    const today = new Date().toISOString().slice(0, 10);
    const overdue = tasks.filter((t: KanbanTask) => t.dueDate && t.dueDate < today && t.status !== 'done' && t.status !== 'history').length;
    try {
      const result = await callAi<{ predictions: Array<{ item: string; urgency: string; estimatedCost: number }> }>(
        'predictMaintenanceNeeds',
        { tasks: tasks.map((t: KanbanTask) => ({ title: t.title, dueDate: t.dueDate, status: t.status })), overdueTasks: overdue }
      );
      if (result) setAiCheckupResult(result);
    } finally {
      setAiCheckupLoading(false);
    }
  };

  const handleTroubleshoot = async (applName: string, applMake: string, applModel: string) => {
    setTroubleshootTarget(applName);
    setTroubleshootLoading(true);
    setTroubleshootResult(null);
    try {
      const result = await callAi<{ diagnosis: string; steps: string[] }>(
        'troubleshootAppliance',
        { applianceName: applName, make: applMake, model: applModel, symptoms: [] }
      );
      if (result) setTroubleshootResult(result);
    } finally {
      setTroubleshootLoading(false);
    }
  };

  const maintenanceData = useMemo(() => (state.maintenanceTasks || []).filter(task =>
    task.templateType === TemplateType.MAINTENANCE
  ), [state.maintenanceTasks]);

  const { tasks, createTask } = useTaskState(
    maintenanceData,
    state.currentUser?.id || 'guest',
    dispatch,
    state.currentHouseholdId,
    state.currentUser?.defaultScope || 'BOTH',
    state.currentUser?.permissions
  );

  // --- 📊 ANALYTICS ENGINE (REAL-TIME DERIVATIONS) ---
  const analytics = useMemo(() => {
    const historical = tasks.filter((t: KanbanTask) => t.status === 'history');

    // 🛡️ Fix: Optional chaining on consumption to prevent crash
    const totalSpend = historical.reduce((acc: number, task: KanbanTask) => {
      const costValue = task.consumption?.cost || 0;
      return acc + costValue;
    }, 0);

    const getHealth = (taskName: string): number => {
      const task = tasks.find((t: KanbanTask) => t.title.toLowerCase().includes(taskName.toLowerCase()));
      if (!task || !task.recurrence?.isEnabled || !task.dueDate) return 100;

      const resetBase = task.recurrence.lastResetDate || task.completedAt || new Date(task.localChangeTimestamp || 0).toISOString();
      if (!resetBase || new Date(resetBase).toString() === 'Invalid Date') return 0;

      const lastReset = new Date(resetBase);
      const now = new Date();
      let expectedNextDueDate = new Date(lastReset);

      const interval = task.recurrence.interval || 1;
      const frequency = task.recurrence.frequency;

      if (frequency === 'years') expectedNextDueDate.setFullYear(expectedNextDueDate.getFullYear() + interval);
      else if (frequency === 'months') expectedNextDueDate.setMonth(expectedNextDueDate.getMonth() + interval);
      else if (frequency === 'weeks') expectedNextDueDate.setDate(expectedNextDueDate.getDate() + (interval * 7));
      else if (frequency === 'days') expectedNextDueDate.setDate(expectedNextDueDate.getDate() + interval);
      else return 100;

      const totalCycleDuration = expectedNextDueDate.getTime() - lastReset.getTime();
      const elapsedCycleDuration = now.getTime() - lastReset.getTime();

      if (totalCycleDuration <= 0) return 0;

      const healthPercentage = 100 - Math.min(100, Math.max(0, (elapsedCycleDuration / totalCycleDuration) * 100));
      return Math.round(healthPercentage);
    };

    return {
      totalSpend,
      historyCount: historical.length,
      activeCount: tasks.filter((t: KanbanTask) => t.status !== 'history' && t.status !== 'finalized').length,
      assetHealth: {
        hvac: getHealth('hvac'),
        vehicle: getHealth('oil') || getHealth('vehicle'),
        appliances: getHealth('filter') || getHealth('dishwasher')
      }
    };
  }, [tasks]);

  useEffect(() => {
    if (activeTab !== 'appliances' || !state.currentHouseholdId) return;
    setAppliancesLoading(true);
    getDocs(collection(db, 'households', state.currentHouseholdId, 'appliances'))
      .then(snap => setAppliances(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))))
      .finally(() => setAppliancesLoading(false));
  }, [activeTab, state.currentHouseholdId]);

  useEffect(() => {
    if (activeTab !== 'budget' || !state.currentHouseholdId) return;
    setBudgetLoading(true);
    Promise.all([
      getDocs(collection(db, 'households', state.currentHouseholdId, 'maintenance')),
      getDocs(collection(db, 'households', state.currentHouseholdId, 'tasks')),
      getDoc(doc(db, 'households', state.currentHouseholdId, 'settings', 'maintenanceBudget')),
    ]).then(([maintSnap, tasksSnap, budgetSnap]) => {
      const maintTasks = maintSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as KanbanTask[];
      const allTasks = tasksSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as KanbanTask[];
      const combined = [...maintTasks, ...allTasks].filter(t =>
        t.templateType === TemplateType.MAINTENANCE &&
        (t.status === 'history' || t.status === 'done') &&
        (t.consumption?.cost || 0) > 0
      );
      setBudgetTasks(combined);
      if (budgetSnap.exists()) {
        const cap = budgetSnap.data()?.annualCap || 0;
        setAnnualBudgetCap(cap);
        setBudgetCapInput(cap > 0 ? String(cap) : '');
      }
    }).finally(() => setBudgetLoading(false));
  }, [activeTab, state.currentHouseholdId]);

  const handleSaveBudgetCap = async () => {
    if (!state.currentHouseholdId) return;
    setIsSavingBudget(true);
    const cap = parseFloat(budgetCapInput) || 0;
    try {
      await setDoc(
        doc(db, 'households', state.currentHouseholdId, 'settings', 'maintenanceBudget'),
        { annualCap: cap },
        { merge: true }
      );
      setAnnualBudgetCap(cap);
    } catch (error) {
      console.error('Error saving budget cap:', error);
      showToast(t('maintenance.budget_save_error', 'Failed to save budget. Please try again.'), 'error');
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleAddAppliance = async () => {
    if (!appName || !appMake || !appModel || !state.currentHouseholdId || !state.currentUser) return;
    setIsSavingAppliance(true);
    try {
      await addDoc(collection(db, 'households', state.currentHouseholdId, 'appliances'), {
        name: appName, make: appMake, model: appModel, serialNumber: appSerial,
        room: appRoom, warrantyExpiresDate: appWarranty || null,
        manualUrl: appManualUrl || null, notes: appNotes || null,
        householdId: state.currentHouseholdId, addedBy: state.currentUser.id, createdAt: serverTimestamp(),
      });
      setAppName(''); setAppMake(''); setAppModel(''); setAppSerial('');
      setAppRoom('Kitchen'); setAppWarranty(''); setAppManualUrl(''); setAppNotes('');
      setShowAddAppliance(false);
      // Reload
      const snap = await getDocs(collection(db, 'households', state.currentHouseholdId, 'appliances'));
      setAppliances(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    } finally {
      setIsSavingAppliance(false);
    }
  };

  const getWarrantyBadge = (warrantyDate?: string) => {
    if (!warrantyDate) return { label: 'Unknown', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800' };
    const months = (new Date(warrantyDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30);
    if (months < 0) return { label: 'Expired', cls: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' };
    if (months < 6) return { label: 'Expiring Soon', cls: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' };
    return { label: 'Active', cls: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' };
  };

  // 1. 🛡️ Permission Access Denied State
  if (!hasActiveHousehold) {
    return (
      <div className="max-w-xl mx-auto py-32 px-6 text-center space-y-8 animate-in fade-in zoom-in-95 duration-500">
        <div className="w-24 h-24 bg-rose-50 dark:bg-rose-900/20 rounded-[2.5rem] flex items-center justify-center mx-auto text-rose-500 shadow-xl shadow-rose-500/10">
          <ShieldAlert className="w-12 h-12" />
        </div>
        <div className="space-y-4">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white">
            {t('maint.access_denied', 'Shared Protocol Required')}
          </h2>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
            Maintenance tracking is a <strong>Household-Only</strong> utility. Personal partitions do not support asset ledgers to maintain data integrity across shared living spaces.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Button
            onClick={() => navigate('/settings')}
            size="lg" shape="pill"
            className="w-full bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs"
          >
            <Home className="w-4 h-4 mr-2" /> {t('maint.setup_household', 'Initialize Household')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            size="lg" shape="pill"
            onClick={() => navigate('/dashboard')}
            className="w-full border-2 border-slate-100 dark:border-slate-800 font-black uppercase tracking-widest text-xs"
          >
            {t('common.return_home', 'Return to Terminal')}
          </Button>
        </div>
      </div>
    );
  }

  // 2. Loading State (only if there is an active household and maintenance tasks are not yet loaded)
  if (state.maintenanceTasks.length === 0 && state.isBooting) { // Only show loading if booting AND maintenance tasks are empty
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand w-8 h-8" />
        <p className="text-2xs font-black uppercase tracking-widest text-slate-400">Loading Ledger...</p>
      </div>
    );
  }

  // 3. Main Interface
  return (
    <div className="max-w-page-full mx-auto space-y-8 p-6 pb-32 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em]">
            {t('maint.subtitle', 'Household Health Registry')}
          </p>
        </div>

        <TabBar
          tabs={([
            { id: 'dashboard', label: 'Stats',      icon: <LayoutDashboard className="w-3.5 h-3.5" /> },
            { id: 'log',       label: 'Actions',    icon: <Wrench className="w-3.5 h-3.5" /> },
            { id: 'schedule',  label: 'Rhythms',    icon: <Calendar className="w-3.5 h-3.5" /> },
            { id: 'history',   label: 'Ledger',     icon: <History className="w-3.5 h-3.5" /> },
            { id: 'appliances',label: 'Appliances', icon: <Cpu className="w-3.5 h-3.5" /> },
            { id: 'budget',    label: 'Budget',     icon: <DollarSign className="w-3.5 h-3.5" /> },
          ] as TabItem[])}
          activeTab={activeTab}
          onChange={id => setActiveTab(id as any)}
          className="w-full md:w-auto"
        />
      </header>

      {/* --- DASHBOARD TAB --- */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <TrendingUp className="w-32 h-32 text-brand" />
            </div>
            <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest mb-8">{t('maint.tco', 'Total Cost of Ownership')}</h3>
            <div className="flex items-end gap-2">
              <span className="text-6xl font-black text-slate-900 dark:text-white leading-none">${analytics.totalSpend.toLocaleString()}</span>
              <span className="text-brand font-bold mb-2 flex items-center gap-1 text-xs">
                <ArrowUpRight className="w-4 h-4" /> 12% vs LY
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-12">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-300 uppercase">Records</p>
                <p className="text-xl font-bold">{analytics.historyCount}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-300 uppercase">Active</p>
                <p className="text-xl font-bold">{analytics.activeCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-8 rounded-[3rem] text-white">
            <h3 className="text-2xs font-black uppercase opacity-50 tracking-widest mb-6">{t('maint.health', 'Asset Health')}</h3>
            <div className="space-y-6">
              {Object.entries(analytics.assetHealth).map(([key, val]) => (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between text-2xs font-black uppercase">
                    <span>{key}</span>
                    <span className={val < 30 ? 'text-rose-500' : 'text-brand'}>{val}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ${val < 30 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]' : 'bg-brand'}`}
                      style={{ width: `${val}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

          {/* AI Checkup Panel */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-[2rem] border border-blue-100 dark:border-blue-800 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand" />
                <span className="text-2xs font-black uppercase tracking-widest text-brand">AI Maintenance Checkup</span>
              </div>
              <button
                onClick={handleAiCheckup}
                disabled={aiCheckupLoading}
                className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover disabled:opacity-60 transition-colors"
              >
                {aiCheckupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {aiCheckupLoading ? 'Analyzing...' : 'Run Checkup'}
              </button>
            </div>
            {aiCheckupResult?.predictions && aiCheckupResult.predictions.length > 0 && (
              <div className="space-y-2">
                {aiCheckupResult.predictions.map((p, i) => (
                  <div key={i} className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-xl px-4 py-3 border border-blue-100 dark:border-blue-800">
                    <div>
                      <p className="text-xs font-bold text-slate-800 dark:text-white">{p.item}</p>
                      <span className={`text-[9px] font-black uppercase tracking-widest ${p.urgency === 'high' ? 'text-rose-500' : p.urgency === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`}>{p.urgency} urgency</span>
                    </div>
                    <span className="text-sm font-black text-slate-900 dark:text-white">${p.estimatedCost}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- LOG TAB --- */}
      {activeTab === 'log' && (
        <div className="space-y-8">
          <NLPMaintenanceInput onConfirm={(data: ParsedMaintenanceIntent) => {
            const { recurrence, consumption } = mapDataToTaskObjects(data);
            createTask(
              data.title,
              TemplateType.MAINTENANCE,
              MAINTENANCE_BOARD_ID,
              data.tags || ['maintenance'],
              (data.scope || 'household') as TaskScope,
              recurrence,
              data.dueDate as string | undefined,
              data.description as string | undefined,
              data.priorityTier as PriorityTier,
              data.cost as number | undefined,
              consumption
            );
          }} />
          <Button variant="outline" onClick={() => setShowManual(true)} className="w-full h-16 rounded-[1.5rem] bg-white dark:bg-slate-900 text-brand border-2 border-slate-100 dark:border-slate-800 shadow-sm font-black uppercase tracking-widest">
            <Plus className="w-5 h-5 mr-2" /> {t('maint.manual_entry', 'Detailed Manual Entry')}
          </Button>
        </div>
      )}

      {/* --- LEDGER TAB --- */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {tasks.filter((t: KanbanTask) => t.status === 'history').map((log: KanbanTask) => (
            <div key={log.id} className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm flex items-center justify-between group hover:border-blue-200 transition-all">
              <div className="flex items-center gap-6">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl text-slate-400 group-hover:text-brand transition-colors">
                  <History className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-black text-lg text-slate-800 dark:text-slate-100 uppercase italic tracking-tighter">{log.title}</h4>
                  <p className="text-2xs font-black text-brand uppercase tracking-widest">
                    {log.completedAt ? new Date(log.completedAt).toLocaleDateString() : 'Historical'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">
                  ${(log.consumption?.cost || 0).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- APPLIANCES TAB --- */}
      {activeTab === 'appliances' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xs font-black uppercase tracking-widest text-slate-400">Appliance Registry</h2>
            <button
              onClick={() => setShowAddAppliance(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Appliance
            </button>
          </div>

          {appliancesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-32 rounded-[2rem] bg-slate-800 animate-pulse" />)}
            </div>
          ) : appliances.length === 0 ? (
            <EmptyState
              icon={<Cpu className="w-8 h-8 text-slate-500" />}
              title="No appliances registered yet"
              description="Track your appliances to monitor warranties and service history."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {appliances.map(appl => {
                const warranty = getWarrantyBadge(appl.warrantyExpiresDate);
                return (
                  <div key={appl.id} className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-2xl">
                        <Cpu className="w-5 h-5 text-brand" />
                      </div>
                      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg ${warranty.cls}`}>{warranty.label}</span>
                    </div>
                    <div>
                      <h4 className="font-black text-slate-900 dark:text-white uppercase italic tracking-tighter">{appl.make} {appl.model}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">{appl.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-lg">{appl.room || 'No Room'}</span>
                      {appl.warrantyExpiresDate && <span className="text-[9px] text-slate-400">Expires {new Date(appl.warrantyExpiresDate).toLocaleDateString()}</span>}
                    </div>
                    <button
                      onClick={() => window.open(`https://www.cpsc.gov/Recalls?q=${encodeURIComponent(`${appl.make || ''} ${appl.model || ''}`.trim())}`, '_blank', 'noopener,noreferrer')}
                      className="w-full mt-2 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-amber-500 transition-colors flex items-center justify-center gap-1 py-1"
                    >
                      <ShieldAlert className="w-3 h-3" /> Check Recalls
                    </button>
                    <button
                      onClick={() => handleTroubleshoot(appl.name, appl.make || '', appl.model || '')}
                      disabled={troubleshootLoading && troubleshootTarget === appl.name}
                      className="w-full text-[9px] font-black uppercase tracking-widest text-brand hover:text-brand-hover transition-colors flex items-center justify-center gap-1 py-1 disabled:opacity-60"
                    >
                      {troubleshootLoading && troubleshootTarget === appl.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      AI Diagnose
                    </button>
                    {troubleshootResult && troubleshootTarget === appl.name && (
                      <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl space-y-2">
                        <p className="text-2xs font-bold text-slate-700 dark:text-slate-300">{troubleshootResult.diagnosis}</p>
                        {troubleshootResult.steps.map((s, i) => (
                          <p key={i} className="text-[9px] text-slate-500">• {s}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Appliance Modal */}
          {showAddAppliance && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddAppliance(false)} />
              <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-lg shadow-2xl space-y-4 mx-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-white">New Appliance</h3>
                  <button onClick={() => setShowAddAppliance(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"><X className="w-4 h-4 text-slate-400" /></button>
                </div>
                {[
                  { label: 'Name', value: appName, set: setAppName, placeholder: 'e.g. Primary Refrigerator' },
                  { label: 'Make', value: appMake, set: setAppMake, placeholder: 'e.g. LG' },
                  { label: 'Model', value: appModel, set: setAppModel, placeholder: 'e.g. LRMVS3006S' },
                  { label: 'Serial Number', value: appSerial, set: setAppSerial, placeholder: 'Optional' },
                  { label: 'Warranty Expires', value: appWarranty, set: setAppWarranty, placeholder: 'YYYY-MM-DD' },
                  { label: 'Manual URL', value: appManualUrl, set: setAppManualUrl, placeholder: 'Optional link' },
                ].map(({ label, value, set, placeholder }) => (
                  <div key={label}>
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 block">{label}</label>
                    <input value={value} onChange={e => set(e.target.value)} placeholder={placeholder}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-brand" />
                  </div>
                ))}
                <div>
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 block">Room</label>
                  <select value={appRoom} onChange={e => setAppRoom(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-brand">
                    {['Kitchen', 'Bathroom', 'Bedroom', 'Living Room', 'Garage', 'Laundry', 'Office', 'Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <button onClick={handleAddAppliance} disabled={!appName || !appMake || !appModel || isSavingAppliance}
                  className="w-full py-3 bg-brand text-white font-black text-2xs uppercase tracking-widest rounded-full hover:bg-brand-hover disabled:opacity-50 transition-colors">
                  {isSavingAppliance ? 'Saving...' : 'Add Appliance'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- BUDGET TAB --- */}
      {activeTab === 'budget' && (
        <div className="space-y-6">
          {(() => {
            const now = new Date();
            const ytdTasks = budgetTasks.filter(t => {
              const d = t.completedAt ? new Date(t.completedAt) : null;
              return d && d.getFullYear() === now.getFullYear();
            });
            const ytdTotal = ytdTasks.reduce((s, t) => s + (t.consumption?.cost || 0), 0);
            const overBudget = annualBudgetCap > 0 && ytdTotal > annualBudgetCap;
            const budgetPct = annualBudgetCap > 0 ? Math.min((ytdTotal / annualBudgetCap) * 100, 100) : 0;

            return (
              <>
                <div className={`p-6 rounded-[2rem] border ${overBudget ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800'}`}>
                  <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-1">YTD Maintenance Spend</p>
                  <p className={`text-4xl font-black ${overBudget ? 'text-red-600' : 'text-slate-900 dark:text-white'}`}>
                    ${ytdTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                  {annualBudgetCap > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-xs font-bold text-slate-500">
                        <span>{budgetPct.toFixed(0)}% of annual budget</span>
                        <span>${annualBudgetCap.toLocaleString()}</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${budgetPct >= 100 ? 'bg-red-500' : budgetPct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{ width: `${budgetPct}%` }}
                        />
                      </div>
                      {overBudget && <p className="text-xs font-black text-red-500 uppercase tracking-widest">⚠ Over budget</p>}
                    </div>
                  )}
                </div>

                {budgetLoading ? (
                  <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />)}</div>
                ) : ytdTasks.length === 0 ? (
                  <EmptyState
                    title="No maintenance costs recorded this year"
                    description="Log maintenance tasks with a cost to see spend analysis."
                    className="py-16"
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-2xs font-black uppercase tracking-widest text-slate-400">Top Spend This Year</p>
                    {[...ytdTasks]
                      .sort((a, b) => (b.consumption?.cost || 0) - (a.consumption?.cost || 0))
                      .slice(0, 8)
                      .map(t => {
                        const cost = t.consumption?.cost || 0;
                        const maxCost = ytdTasks.reduce((m, x) => Math.max(m, x.consumption?.cost || 0), 1);
                        const pct = (cost / maxCost) * 100;
                        return (
                          <div key={t.id} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
                            <div className="flex justify-between mb-2">
                              <span className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate flex-1">{t.title}</span>
                              <span className="text-sm font-black text-slate-900 dark:text-white ml-4">${cost.toLocaleString()}</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </>
            );
          })()}

          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2rem] p-6">
            <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mb-4">Annual Budget Cap</p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-500 mb-1 block">Cap amount ($)</label>
                <input
                  type="number"
                  value={budgetCapInput}
                  onChange={e => setBudgetCapInput(e.target.value)}
                  placeholder="e.g. 5000"
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-brand"
                />
              </div>
              <button
                onClick={handleSaveBudgetCap}
                disabled={isSavingBudget}
                className="px-5 py-3 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {isSavingBudget ? 'Saving…' : 'Save'}
              </button>
            </div>
            {annualBudgetCap > 0 && <p className="text-xs text-slate-400 mt-2">Current cap: ${annualBudgetCap.toLocaleString()}/year</p>}
          </div>
        </div>
      )}

      {/* Manual Entry Drawer */}
      {showManual && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowManual(false)} />
          <div className="relative w-full max-w-xl bg-white dark:bg-slate-950 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 ease-out">
            <HistoricalTaskCreator
              onClose={() => setShowManual(false)}
              onAdd={(data: any) => {
                const { recurrence, consumption } = mapDataToTaskObjects(data);
                createTask(
                  data.title,
                  TemplateType.MAINTENANCE,
                  MAINTENANCE_BOARD_ID,
                  data.tags || ['maintenance'],
                  (data.scope || 'household') as TaskScope,
                  recurrence,
                  data.dueDate as string | undefined,
                  data.description as string | undefined,
                  data.priorityTier as PriorityTier,
                  data.cost as number | undefined,
                  consumption
                );
                setShowManual(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};